/**
 * Service for interacting with the Cloudflare R2 URL Worker
 */
import * as Sentry from "@sentry/browser";
import { getAuth } from "firebase/auth";

// URL of the deployed Cloudflare worker
const CLOUDFLARE_WORKER_URL = "https://kancho-r2-url-worker.kanchov.workers.dev/getR2Url";

// Check if we're in the browser environment
const isBrowser = typeof window !== "undefined";

/**
 * Get a signed download URL from the Cloudflare R2 worker
 * 
 * @param {Object} params - Parameters for the download
 * @param {string} params.currentDownload - The video ID
 * @param {string} params.video - The video path
 * @param {string} params.fileName - The filename for the download
 * @returns {Promise<string>} The signed download URL
 */
export const getSignedDownloadUrl = async ({ currentDownload, video, fileName }) => {
  try {
    // Make sure we're in a browser environment
    if (!isBrowser) {
      throw new Error('Cannot make fetch requests in a non-browser environment');
    }

    // Get the current user's ID token for authentication
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Get the user's ID token
    const token = await user.getIdToken();
    
    // Add a cache-busting timestamp parameter to the URL
    const cacheBuster = `?t=${Date.now()}`;
    const urlWithCacheBuster = `${CLOUDFLARE_WORKER_URL}${cacheBuster}`;
    
    // Call the Cloudflare worker directly
    const response = await fetch(urlWithCacheBuster, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Origin': window.location.origin,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      },
      mode: 'cors',
      credentials: 'same-origin',
      body: JSON.stringify({
        currentDownload,
        video,
        fileName
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get download URL: ${errorText}`);
    }
    
    const data = await response.json();
    
    // If there's an error message from the worker, throw it as an error
    if (data.error) {
      throw new Error(data.error);
    }
    
    // If no URL is returned, throw a generic error
    if (!data.url) {
      throw new Error('No download URL available. Please try again later.');
    }
    
    return data.url;
  } catch (error) {
    console.error("Error getting signed download URL:", error);
    Sentry.captureException(error);
    throw error;
  }
};
